import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import LiveFeedCard from './LiveFeedCard';
import LiveFeedFilters, {
  EMPTY_FEED_FILTERS,
  feedFiltersKey,
  filtersFromSearchParams,
  filtersToSearchParams,
  isFiltered,
  toFeedQuery
} from './LiveFeedFilters';
import { CreatePost } from '../components/forms/CreatePost';
import Modal from '../../shared/components/ui/Modal';
import { getFeedPage, setPostHidden } from '../services/api/implementation/impl';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import socket from '../../../socket';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { buildReturnToFromLocation, navigateWithReturn } from '../../shared/navigation';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ArrowUp, PenSquare, Radio, Rss } from 'lucide-react';

// Within this distance of the top the user is "reading the latest".
const NEAR_TOP_PX = 200;
const PAGE_SIZE = 50;
// A press that moves further than this is a swipe (the image slider), not a
// click that opens the post.
const CLICK_SLOP_PX = 6;
// Where the feed was scrolled to when a post was opened, per filter set.
const SCROLL_KEY = 'liveFeedScroll';

const FeedSkeleton = () => (
  <div
    aria-hidden="true"
    className="flex h-72 animate-pulse flex-col gap-4 rounded-panel border border-hairline bg-surface p-5 shadow-sm">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-full bg-surface-sunken" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-1/3 rounded bg-surface-sunken" />
        <div className="h-3 w-1/4 rounded bg-surface-sunken" />
      </div>
    </div>
    <div className="h-5 w-28 rounded-pill bg-surface-sunken" />
    <div className="space-y-2">
      <div className="h-3 rounded bg-surface-sunken" />
      <div className="h-3 w-5/6 rounded bg-surface-sunken" />
      <div className="h-3 w-2/3 rounded bg-surface-sunken" />
    </div>
  </div>
);

// Every cached feed page, whatever the filters, with `update` applied to each
// post (return the post unchanged, a new post, or null to drop it).
const mapFeedPosts = (queryClient, update) =>
  queryClient.setQueriesData({ queryKey: ['feed'] }, (old) =>
    old
      ? {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            posts: page.posts.map(update).filter(Boolean)
          }))
        }
      : old
  );

const LiveFeedList = () => {
  const userId = Cookies.get('userId');
  const queryClient = useQueryClient();
  const [isCreatePost, setisCreatePost] = useState(false);
  const [createPostDirty, setCreatePostDirty] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editPostDirty, setEditPostDirty] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed } = useSidebar();
  const [newPostCount, setNewPostCount] = useState(0);
  // Filters live in the URL (?project=&types=&q=&range=|from=&to=&mentions=1),
  // so a filtered feed survives a reload and can be shared.
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => filtersFromSearchParams(searchParams));
  // The filters last written to (or read from) the URL, so the URL watcher
  // below ignores our own writes, which land a tick after the state change.
  const urlFiltersKey = useRef(feedFiltersKey(filters));
  const feedQuery = useMemo(() => toFeedQuery(filters), [filters]);
  const feedKey = useMemo(() => ['feed', feedQuery], [feedQuery]);
  const reloadTimer = useRef(null);
  const checkTimer = useRef(null);
  const pointer = useRef(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isPending,
    isError,
    refetch
  } = useInfiniteQuery({
    queryKey: feedKey,
    queryFn: ({ pageParam }) => getFeedPage(pageParam, PAGE_SIZE, feedQuery),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => (lastPage.hasMore ? pages.length + 1 : undefined),
    // The app-wide default keeps data for 100 minutes; the feed refetches
    // whenever it's shown again. The cached pages render meanwhile, which is
    // what lets the scroll position come back after opening a post.
    staleTime: 0,
    // A focus refetch would reload every loaded page; sockets keep it live.
    refetchOnWindowFocus: false
  });

  // New posts arriving between page requests shift the pages, so a post can
  // come back on the next page; show it once.
  const posts = useMemo(() => {
    const seen = new Set();
    const list = [];
    (data?.pages || []).forEach((page) =>
      page.posts.forEach((post) => {
        if (!seen.has(post.id)) {
          seen.add(post.id);
          list.push(post);
        }
      })
    );
    return list;
  }, [data]);
  const postsRef = useRef(posts);
  postsRef.current = posts;

  const patchPost = useCallback(
    (postId, patch) =>
      mapFeedPosts(queryClient, (post) =>
        post.id === postId
          ? { ...post, ...(typeof patch === 'function' ? patch(post) : patch) }
          : post
      ),
    [queryClient]
  );

  const removePost = useCallback(
    (postId) => mapFeedPosts(queryClient, (post) => (post.id === postId ? null : post)),
    [queryClient]
  );

  // Back to page 1 from the server, dropping the loaded pages.
  const reloadFeed = useCallback(() => {
    setNewPostCount(0);
    queryClient.resetQueries({ queryKey: ['feed'] });
  }, [queryClient]);

  // A new comment (from the socket) bumps the post's count, once per comment
  // even if the event arrives twice.
  const seenComments = useRef(new Set());
  const addComment = useCallback(
    (comment) => {
      if (!comment?.post_id) return;
      if (comment.id != null) {
        if (seenComments.current.has(comment.id)) return;
        seenComments.current.add(comment.id);
      }
      patchPost(comment.post_id, (post) => ({
        comment_count: (post.comment_count || 0) + 1
      }));
    },
    [patchPost]
  );

  // How many posts on the server's first page, for these filters, the user
  // hasn't loaded yet and didn't write. Asked once per burst of Post events.
  const countNewPosts = useCallback(async () => {
    try {
      const { posts: latest } = await getFeedPage(1, 20, feedQuery);
      const loaded = postsRef.current;
      const loadedIds = new Set(loaded.map((post) => post.id));
      const newestLoaded = loaded.reduce((max, post) => Math.max(max, post.id || 0), 0);
      setNewPostCount(
        latest.filter(
          (post) =>
            !loadedIds.has(post.id) &&
            post.id > newestLoaded &&
            String(post.created_by) !== String(userId)
        ).length
      );
    } catch {
      // The pill is a hint; a failed check leaves it as it was.
    }
  }, [feedQuery, userId]);

  // One set of socket listeners for the whole feed, not three per card.
  useEffect(() => {
    const handlePost = () => {
      if (window.scrollY < NEAR_TOP_PX) {
        // At the top: one reload per burst (a bulk action can emit dozens).
        clearTimeout(reloadTimer.current);
        reloadTimer.current = setTimeout(reloadFeed, 1000);
      } else {
        clearTimeout(checkTimer.current);
        checkTimer.current = setTimeout(countNewPosts, 1500);
      }
    };
    const handleComment = (comment) => addComment(comment);
    const handleLike = (res) => {
      if (res?.post_id != null && typeof res.count === 'number') {
        patchPost(res.post_id, { like_count: res.count });
      }
    };
    socket.on('Post', handlePost);
    socket.on('Comment', handleComment);
    socket.on('Like', handleLike);
    return () => {
      socket.off('Post', handlePost);
      socket.off('Comment', handleComment);
      socket.off('Like', handleLike);
      clearTimeout(reloadTimer.current);
      clearTimeout(checkTimer.current);
    };
  }, [reloadFeed, countNewPosts, addComment, patchPost]);

  // New filters mean a new list, which is a new query key.
  const resetToFilters = (next) => {
    setFilters(next);
    setNewPostCount(0);
  };

  // Replace, not push: each filter tweak shouldn't become a Back step. Commas
  // are left unescaped so the URL reads types=lead,deal.
  const applyFilters = (next) => {
    resetToFilters(next);
    urlFiltersKey.current = feedFiltersKey(next);
    const query = filtersToSearchParams(next, searchParams).toString().replace(/%2C/gi, ',');
    navigate({ search: query ? `?${query}` : '' }, { replace: true, state: location.state });
  };

  // The URL changed under us (Back/Forward, or a link to the feed): follow it.
  const search = searchParams.toString();
  useEffect(() => {
    const fromUrl = filtersFromSearchParams(new URLSearchParams(search));
    const key = feedFiltersKey(fromUrl);
    if (key === urlFiltersKey.current) return;
    urlFiltersKey.current = key;
    resetToFilters(fromUrl);
  }, [search]);

  const showNewPosts = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    reloadFeed();
  };

  // Track page view
  useEffect(() => {
    mixpanel.track('Live Feed List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Coming back from a post: the cached pages are on screen, so return to
  // where the user was. Only for the same filters, and only once.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || posts.length === 0) return;
    restored.current = true;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      sessionStorage.removeItem(SCROLL_KEY);
      if (saved?.feed === feedFiltersKey(filters) && saved.y > 0) {
        requestAnimationFrame(() => window.scrollTo(0, saved.y));
      }
    } catch {
      // Storage can be unavailable (private mode); starting at the top is fine.
    }
  }, [posts.length, filters]);

  const handleOnCreatePost = () => {
    setisCreatePost(!isCreatePost);
  };

  const onPostCreate = () => {
    setisCreatePost(false);
    reloadFeed();
  };

  const onPostEdited = (saved) => {
    if (saved?.id != null) {
      patchPost(saved.id, {
        post: saved.post,
        title: saved.title,
        is_public: saved.is_public,
        attachments: saved.attachments,
        tagPostMapping: saved.tagPostMapping
      });
    }
    setEditingPost(null);
  };

  const onPostHidden = (postId) => {
    removePost(postId);
    toast((t) => (
      <span className="flex items-center gap-3 text-sm">
        Post hidden from your feed.
        <button
          type="button"
          className="font-medium text-brand-700 hover:underline"
          onClick={async () => {
            toast.dismiss(t.id);
            try {
              await setPostHidden(postId, false);
              reloadFeed();
            } catch {
              toast.error('Could not undo. Please try again.');
            }
          }}>
          Undo
        </button>
      </span>
    ));
  };

  // `focusComment` lands on the post page's comment box (the card's Comment
  // button); comments are written there, not on the card.
  const openPost = (postId, { focusComment = false } = {}) => {
    try {
      sessionStorage.setItem(
        SCROLL_KEY,
        JSON.stringify({ feed: feedFiltersKey(filters), y: window.scrollY })
      );
    } catch {
      // See above: without storage the feed just opens at the top.
    }
    navigateWithReturn(navigate, `/builder/post/${postId}`, {
      returnTo: buildReturnToFromLocation(location),
      ...(focusComment && { state: { focusComment: true } })
    });
  };

  const loadMore = () => {
    if (!isFetching && !isError && hasNextPage) fetchNextPage();
  };

  const modalOpen = isCreatePost || !!editingPost;
  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  const loadingFirstPage = posts.length === 0 && (isPending || (isFetching && !isError));

  return (
    <>
      {/* Create Post Modal */}
      {isCreatePost && (
        <Modal
          isDirty={createPostDirty}
          confirmOnClose={true}
          name={'Create New Post'}
          width={50}
          onClose={() => setisCreatePost(!isCreatePost)}>
          <CreatePost
            onSuccess={onPostCreate}
            onClose={() => setisCreatePost(!isCreatePost)}
            onDirtyChange={setCreatePostDirty}
          />
        </Modal>
      )}
      {editingPost && (
        <Modal
          isDirty={editPostDirty}
          confirmOnClose={true}
          name={'Edit Post'}
          width={50}
          onClose={() => setEditingPost(null)}>
          <CreatePost
            post={editingPost}
            onSuccess={onPostEdited}
            onClose={() => setEditingPost(null)}
            onDirtyChange={setEditPostDirty}
          />
        </Modal>
      )}

      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            data={{
              name: 'Live Feed',
              subtitle: 'Activity across your projects, as it happens.',
              icon: <Radio size={20} />
            }}
          />
        </div>
        {newPostCount > 0 && (
          <div
            className={`fixed top-[5rem] z-[11] flex justify-center pointer-events-none transition-all duration-300 ease-in-out ${
              collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            }`}>
            <button
              type="button"
              onClick={showNewPosts}
              className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-4 py-2 text-sm font-medium text-white shadow-md hover:bg-brand-600">
              <ArrowUp size={15} />
              {newPostCount} new {newPostCount === 1 ? 'post' : 'posts'}
            </button>
          </div>
        )}
        <GridWrapper>
          <div className="mx-auto mt-[5rem] flex max-w-[1400px] items-start gap-6">
            <div className="min-w-0 flex-1">
              <LiveFeedFilters
                filters={filters}
                onChange={applyFilters}
                actions={
                  <button
                    type="button"
                    onClick={handleOnCreatePost}
                    className="inline-flex h-10 shrink-0 items-center gap-2 rounded-control bg-brand-700 px-4 text-sm font-medium text-white shadow-sm hover:bg-brand-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600">
                    <PenSquare size={16} />
                    New Post
                  </button>
                }
              />
              <InfiniteScroll
                dataLength={posts.length}
                next={loadMore}
                hasMore={!!hasNextPage && !isError}
                loader={
                  isFetchingNextPage && (
                    <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-100 border-t-brand-700"></div>
                      Loading more posts…
                    </div>
                  )
                }
                endMessage={
                  posts.length > 0 &&
                  (isError ? (
                    <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                      Couldn&apos;t load more posts.
                      <button
                        type="button"
                        onClick={() => refetch()}
                        className="font-medium text-brand-700 hover:underline">
                        Try again
                      </button>
                    </div>
                  ) : (
                    <div className="mx-auto flex max-w-[640px] items-center gap-3 py-6 text-xs text-gray-400">
                      <span className="h-px flex-1 bg-hairline" />
                      You&apos;re all caught up
                      <span className="h-px flex-1 bg-hairline" />
                    </div>
                  ))
                }
                style={{ overflow: 'visible' }}>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {posts.length > 0 ? (
                    posts.map((post) => (
                      <div
                        key={post.id}
                        role="link"
                        tabIndex={0}
                        aria-label={`Open post by ${post.creater_name || 'someone'}`}
                        className="h-full rounded-panel focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                        onPointerDown={(e) => {
                          pointer.current = { x: e.clientX, y: e.clientY };
                        }}
                        onClick={(e) => {
                          const start = pointer.current;
                          pointer.current = null;
                          if (
                            start &&
                            Math.hypot(e.clientX - start.x, e.clientY - start.y) > CLICK_SLOP_PX
                          ) {
                            return;
                          }
                          openPost(post.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openPost(post.id);
                          }
                        }}>
                        <LiveFeedCard
                          data={post}
                          onComment={() => openPost(post.id, { focusComment: true })}
                          onChanged={(patch) => patchPost(post.id, patch)}
                          onEdit={() => setEditingPost(post)}
                          onHidden={() => onPostHidden(post.id)}
                          onPinnedChange={reloadFeed}
                        />
                      </div>
                    ))
                  ) : loadingFirstPage ? (
                    <>
                      <span className="sr-only" role="status">
                        Loading the feed…
                      </span>
                      <FeedSkeleton />
                      <FeedSkeleton />
                      <FeedSkeleton />
                      <FeedSkeleton />
                    </>
                  ) : isError ? (
                    <div className="col-span-full mx-auto flex w-full max-w-[640px] flex-col items-center rounded-panel border border-dashed border-hairline-strong bg-surface px-6 py-14 text-center">
                      <p className="text-sm font-semibold text-gray-900">
                        Couldn&apos;t load the feed
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Check your connection and try again.
                      </p>
                      <button
                        type="button"
                        onClick={() => refetch()}
                        className="mt-2 text-sm font-medium text-brand-700 hover:underline">
                        Try again
                      </button>
                    </div>
                  ) : (
                    <div className="col-span-full mx-auto flex w-full max-w-[640px] flex-col items-center rounded-panel border border-dashed border-hairline-strong bg-surface px-6 py-14 text-center">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                        <Rss size={20} />
                      </span>
                      {isFiltered(filters) ? (
                        <>
                          <p className="mt-3 text-sm font-semibold text-gray-900">
                            {filters.mentions && !isFiltered({ ...filters, mentions: false })
                              ? 'No mentions yet'
                              : 'Nothing matches these filters'}
                          </p>
                          {filters.mentions && (
                            <p className="mt-1 text-sm text-gray-500">
                              Posts where someone tags you, in the post or a comment, show up
                              here.
                            </p>
                          )}
                          <button
                            type="button"
                            onClick={() => applyFilters(EMPTY_FEED_FILTERS)}
                            className="mt-2 text-sm font-medium text-brand-700 hover:underline">
                            Clear filters
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="mt-3 text-sm font-semibold text-gray-900">
                            Nothing in the feed yet
                          </p>
                          <p className="mt-1 text-sm text-gray-500">
                            Activity across your projects will show up here as it happens.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </InfiniteScroll>
            </div>
          </div>
        </GridWrapper>
      </Layout>
    </>
  );
};

export default LiveFeedList;
